/**
 * M2 Menu Planning — frontend resource definition (issue #156 sub-PR #1).
 *
 * Read-only path: list + detail. Write paths (createDraft / approve /
 * publish / unpublish / amend / republish-fanout) are deferred to
 * sub-PRs #2-5 because they each need dedicated dialogs with their
 * own validation + confirmation UX (per the design doc §M2):
 *
 *   - sub-PR 2: Week page (drag-drop calendar with upsertItem)
 *   - sub-PR 3: Review page (needs/cost projection)
 *   - sub-PR 4: Publish dialog (publish + emit_failed UX + republish retry)
 *   - sub-PR 5: Amend dialog (M2.5 amend window client-side check)
 *
 * This PR ships the foundation: an entity definition + ResourcePage
 * wiring so ops can browse the plan list and read individual plans.
 *
 * Re-authored against the resource-kit v2 model/view contracts and consumed
 * directly as a `ResourceBundle` by `ResourcePage` (#1805 slice A2). No cell
 * projection moved: every column already rendered
 * through its field type, so this resource owns no widgets. What DID need a
 * carrier is `detail.title` — see
 * `packages/resource-kit-controllers/react/bundle.ts`.
 *
 * The detail endpoint returns `MenuPlanWithItems` (envelope + items
 * array). The resource's generic row type is `T`, so a tab's `render`
 * receives the augmented row at runtime even though TS narrows it to
 * `MenuPlan`. The MenuItemsCard component casts inside its boundary —
 * same pattern catalog's `RecipeEditorCard` uses to access
 * `dish.ingredients`.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle, DataTableParams } from '@resource-kit/controllers/react';
import { filterOp, type FilterWhitelist } from '@resource-kit/controllers';
import type { MenuPlan, MenuPlanStatus, MenuPlanListParams, MenuPlanWithItems } from '@/services';
import { menuPlanAPI, MENU_PLAN_STATUSES, MENU_ITEM_STATUSES, toListResult } from '@/services';
import { PERMISSIONS } from '@/permissions';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { MenuItemsCard } from './components/MenuItemsCard';
import { LifecycleActionsToolbar } from './components/LifecycleActionsToolbar';
import { WeekViewLink } from './components/WeekViewLink';

/**
 * Bridge the shared `DataTableParams` (page/limit/sort/search/filters) to
 * the bespoke `/api/menu/plans` query. The menu list route is NOT the
 * generic list kernel: it takes a bare `status` enum + a `week_from`/
 * `week_to` range + `page`/`size` — no `sort`/`search`. `limit` maps to
 * `size` (dropping it would silently cap the page at the backend default
 * of 20 and hide later rows). `status` may arrive as a serialized filter
 * value (`eq:draft`).
 *
 * The bespoke route filters `status` by EQUALITY only — there is no `ne`/
 * `in`/range equivalent. So classify the op with the shared `filterOp`
 * (mirrors the kernel's `parseFilterValues`) and forward the value only
 * when the op is `eq`. Any other op is dropped, NOT reinterpreted:
 * blindly stripping the `<op>:` prefix turned `ne:draft` into an
 * `eq:draft` query (a silent semantic inversion) and swallowed
 * `in:draft,approved` whole. The `filterable` whitelist declared on the
 * resource surfaces such dropped filters as a visible warning (#1154).
 */
export function mapListParams(p: DataTableParams): MenuPlanListParams {
  const out: MenuPlanListParams = { page: p.page, size: p.limit };
  const rawStatus = p.extraParams?.status;
  if (rawStatus && filterOp(rawStatus) === 'eq') {
    // op is `eq` → the value is bare or carries a recognized `eq:` prefix.
    const colon = rawStatus.indexOf(':');
    const value =
      colon > 0 && rawStatus.slice(0, colon).toLowerCase() === 'eq'
        ? rawStatus.slice(colon + 1)
        : rawStatus;
    if ((MENU_PLAN_STATUSES as readonly string[]).includes(value)) {
      out.status = value as MenuPlanStatus;
    }
  }
  return out;
}

// The bespoke `/api/menu/plans` route accepts a bare `status` enum filtered
// by equality only. Declaring the whitelist here lets ResourcePage's
// dropped-filter warning (#1154) fire when the operator picks an op the
// route cannot honour (`ne`/`in`/range) — without it, menu plans would
// never trigger that signal and the dropped filter stayed invisible.
const MENU_PLAN_FILTERS: FilterWhitelist = { status: ['eq'] };

// Status badge colours — closed-set derived from MENU_PLAN_STATUSES so a
// new status added to the backend `as const` tuple forces a frontend
// match here (the `Record<MenuPlanStatus, ...>` exhaustiveness check
// fails to compile when keys are missing). EQD §1 — no magic-string
// drift between status string and the colour map.
const STATUS_COLORS: Record<MenuPlanStatus, EnumOption['color']> = {
  draft: 'secondary',
  approved: 'warning',
  published: 'success',
};

// Exported (PR #353 — #345 fold-in follow-up to #351) so the week
// page + items card can route the `plan.status` / `item.status`
// wire values through the same SOT instead of building raw
// template-literal i18n keys. Module-boundary discipline (Core
// Philosophy §1): menu-planning owns the option SOT; sibling
// pages/components in this feature read it from here rather than
// re-declaring per-call-site.
export const menuPlanStatusOptions: EnumOption[] = MENU_PLAN_STATUSES.map((status) => ({
  value: status,
  label: `menu_plan.enum.status.${status}`,
  color: STATUS_COLORS[status],
}));

// `MenuItem.status` option mirror — driven by the same `as const`
// tuple that the backend SOT pin uses. Adding a new value to
// `MENU_ITEM_STATUSES` forces the `.map` to pick it up; the
// architecture test `menu-planning-enums-sync.test.ts` enforces
// frontend ↔ backend equality.
export const menuItemStatusOptions: EnumOption[] = MENU_ITEM_STATUSES.map((status) => ({
  value: status,
  label: `menu_plan.enum.itemStatus.${status}`,
}));

const f = createFieldConstructors<MenuPlan>();

export const menuPlanModel = defineModel<MenuPlan>()({
  name: 'menu_plan',
  idField: 'id',
  // The week a plan covers is what an operator calls it. `recName` has no
  // consumer on the detail surface yet — the header title travels on the host,
  // see `packages/resource-kit-controllers/react/bundle.ts` — so this is the
  // value Stage B will resolve it to, and it matches what `detail.title`
  // returns today.
  recName: 'week_start',
  fields: {
    id: f.id({ label: 'menu_plan.field.id' }),
    week_start: f.date({ label: 'menu_plan.field.weekStart' }),
    status: f.enum({ label: 'menu_plan.field.status', options: menuPlanStatusOptions }),
    approved_by: f.text({ label: 'menu_plan.field.approvedBy' }),
    approved_at: f.datetime({ label: 'menu_plan.field.approvedAt' }),
    published_at: f.datetime({ label: 'menu_plan.field.publishedAt' }),
    created_at: f.datetime({ label: 'menu_plan.field.createdAt' }),
    updated_at: f.datetime({ label: 'menu_plan.field.updatedAt' }),
  },
});

export const menuPlanListView = defineListView(menuPlanModel, {
  items: [
    { field: 'id', columnInvisible: true, invisible: true },
    { field: 'week_start' },
    { field: 'status' },
    { field: 'approved_by', columnInvisible: true },
    { field: 'approved_at', columnInvisible: true },
    { field: 'published_at' },
    { field: 'created_at', columnInvisible: true, invisible: true },
    { field: 'updated_at', columnInvisible: true, invisible: true },
  ],
  defaultSort: { field: 'week_start', dir: 'desc' },
  // `capabilities.search` is deliberately absent, not `false`: the bespoke
  // `/api/menu/plans` route takes no `search` param and the definition never
  // declared `searchable`.
});

export const menuPlanFormView = defineFormView(menuPlanModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'identity',
      label: 'menu_plan.group.identity',
      items: [{ field: 'week_start' }, { field: 'status' }],
    },
    {
      kind: 'fields',
      id: 'lifecycle',
      label: 'menu_plan.group.lifecycle',
      items: [{ field: 'approved_by' }, { field: 'approved_at' }, { field: 'published_at' }],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'menu_plan.group.meta',
      // `id` sits with the other server-assigned stamps rather than in
      // `identity`: it is a read-only UUID, and `identity` is the edit form.
      items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
    },
    // The plan's items are its body. Since #1918 the three `fields` groups
    // above are rail tabs too and are declared first, so the rail opens on
    // `identity` and the items sit one click away. `id` is in no `fields` tab,
    // which is unchanged: it renders in the detail card's trailing "other"
    // section.
    {
      kind: 'custom',
      id: 'items',
      // A rail label takes no interpolation — the card inside keeps the
      // counted heading.
      label: 'menu_plan.items.tabLabel',
      render: function MenuPlanItemsTab({ record }) {
        return <MenuItemsCard plan={record} />;
      },
    },
  ],
});

export const menuPlanResource: ResourceBundle<MenuPlan, typeof menuPlanModel.fields> = {
  model: menuPlanModel,
  list: menuPlanListView,
  form: menuPlanFormView,
  host: {
    api: {
      // Read paths only. The `/api` list page is renamed to the controllers'
      // `ListResult` here (#2167 PR-2); the detail folds `{ plan, items }`
      // into `MenuPlanWithItems` so the items tab and MenuItemsCard read
      // `items` off the row (their existing cast).
      list: async (params) => toListResult(await menuPlanAPI.list(mapListParams(params))),
      get: async (id) => {
        const { plan, items } = await menuPlanAPI.detail(id);
        return { ...plan, items } satisfies MenuPlanWithItems;
      },
      // Write paths (create / update / delete) intentionally omitted —
      // ResourcePage hides the "+ New" + delete buttons when those
      // callbacks aren't supplied. Dedicated dialogs own the lifecycle UX
      // (createDraft / approve / publish / etc.) so the generic CRUD form
      // would have been actively wrong here.
    },
    label: 'menu_plan.label',
    slug: 'menu-planning-plans',
    listTitle: 'menu_plan.listTitle',
    listDescription: 'menu_plan.listDescription',
    // menu plans re-gated onto `catalog:read` — the backend folded the
    // legacy `menu_plans:*` keys into the unified `catalog:*` module (#943).
    permission: {
      list: PERMISSIONS.CATALOG_READ,
      read: PERMISSIONS.CATALOG_READ,
    },
    filterable: MENU_PLAN_FILTERS,
    // `/api/menu/plans` is bespoke: `status` / `week_from` / `week_to` /
    // `page` / `size`, no sort param, and `mapListParams` has nowhere to put
    // one. Declared empty rather than left absent because on this seam absent
    // used to mean "unrestricted" — `createColumns` marked every header
    // sortable and each click reordered nothing (#2114). `list.defaultSort`
    // (`week_start:desc`) is unaffected: `isSortWhitelisted` prunes only the
    // URL-carried and persisted values, never the initial one.
    sortable: [],
    searchPlaceholder: 'menu_plan.searchPlaceholder',
    detail: {
      title: (plan) => plan.week_start,
      // Pre-#345: returned the raw i18n key string
      // (`menu_plan.enum.status.draft`) verbatim — never called `i18n.t`. Fix
      // routes through the SOT option table the field-level `enum` config
      // already uses, so a backend-only status addition fails LOUD here instead
      // of leaking an un-translated key into the operator-facing header. EQD §3.
      subtitle: (plan) =>
        lookupEnumLabelOrWarn(plan.status, menuPlanStatusOptions, 'menu plan status'),
      // The lifecycle transitions and the week-view jump are the plan's
      // commands and its navigation: they belong in the header, which stays on
      // screen whichever tab the rail shows (#1804).
      headerActions: (plan) => (
        <>
          <WeekViewLink planId={plan.id} />
          <LifecycleActionsToolbar plan={plan} />
        </>
      ),
    },
  },
};
